/**
 * Entry points for remote device sessions (ENG-2470 Phase 2). Shown only
 * when the site's About says `device_session_available` (switched on, and
 * this user may open sessions at this site), so a button never leads to a
 * refusal that was knowable up front.
 *
 *  - `DeviceSessionButton`: one camera ("Open device" next to Test stream /
 *    Watch live).
 *  - `ServerDeviceSessionButton`: the site's NVR / recorder ("Open NVR"),
 *    for sites whose cameras are reached through the server rather than at
 *    their own address (`server_open_device_available`, ENG-2507). Cameras
 *    whose `open_device_target` is "server" hide their own button.
 *  - `SiteDeviceSessionButton`: pick any of a site's devices (camera, VMS/NVR
 *    server, management, event or recording server) from the same list the
 *    Troubleshooter's target picker uses.
 */
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import { TsButton } from "../../Troubleshooting/TroubleshootingUi";
import { DeviceSessionDialog, OPEN_DEVICE_LABEL } from "./DeviceSessionDialog";
import { UseHttpsCheckbox } from "./UseHttpsCheckbox";
import { defaultUseHttps } from "./deviceSessionApi";
import {
  cameraTarget,
  fetchSiteDeviceTargets,
  SOURCE_LABEL,
} from "./deviceSessionTargets";
import { DeviceSessionTarget } from "./deviceSessionTypes";

/** The site About's `device_session_available`: anything but true hides. */
export const isDeviceSessionAvailable = (
  available: boolean | null | undefined
): boolean => available === true;

/**
 * Where a camera's "Open device" goes (admin `open_device_target`): "server"
 * means the camera is reached through the site's NVR / recorder, so the
 * site-level button opens it instead. Missing means "camera".
 */
export type OpenDeviceTarget = "server" | "camera";

/**
 * Integration types whose server is an NVR or DVR (rather than a VMS), so
 * the site button says "Open NVR". Anything else says "Open recorder".
 */
const NVR_DVR_INTEGRATIONS = new Set(["openeye", "adpro"]);

export const isNvrDvrIntegration = (
  integrationType: string | null | undefined
): boolean => {
  const name = (integrationType ?? "").toLowerCase();
  return (
    NVR_DVR_INTEGRATIONS.has(name) || /(^|[^a-z])(nvr|dvr)([^a-z]|$)/.test(name)
  );
};

export const serverOpenDeviceLabel = (
  integrationType: string | null | undefined
): string =>
  isNvrDvrIntegration(integrationType) ? "Open NVR" : "Open recorder";

/**
 * "Use HTTPS" beside a launcher: starts from the port's usual scheme and is
 * only sent once the user changes it (admin otherwise picks the scheme).
 */
const useStartScheme = (
  defaultPort?: number | null,
  defaultScheme?: string | null
) => {
  const [chosen, setChosen] = React.useState<boolean | null>(null);
  const checked = chosen ?? defaultUseHttps(defaultPort, defaultScheme);
  const scheme: "http" | "https" | undefined =
    chosen == null ? undefined : chosen ? "https" : "http";
  return { checked, scheme, setChosen };
};

export const DeviceSessionButton = ({
  available,
  siteId,
  cameraId,
  cameraName,
  openDeviceTarget,
  defaultPort,
  defaultScheme,
  size = "sm",
  variant = "outline",
}: {
  available: boolean | null | undefined;
  siteId: number;
  cameraId: number;
  cameraName?: string | null;
  /** admin's `open_device_target`; "server" hides this camera's button. */
  openDeviceTarget?: OpenDeviceTarget | string | null;
  /** The web port / scheme it will open, when known: "Use HTTPS" default. */
  defaultPort?: number | null;
  defaultScheme?: string | null;
  size?: "sm" | "md";
  variant?: "outline" | "quiet";
}) => {
  const [open, setOpen] = React.useState(false);
  const https = useStartScheme(defaultPort, defaultScheme);
  const target = React.useMemo(
    () => ({
      ...cameraTarget(siteId, cameraId, cameraName),
      ...(https.scheme ? { scheme: https.scheme } : {}),
    }),
    [siteId, cameraId, cameraName, https.scheme]
  );
  if (
    openDeviceTarget === "server" ||
    !isDeviceSessionAvailable(available) ||
    !Number.isFinite(siteId) ||
    !Number.isFinite(cameraId)
  ) {
    return null;
  }
  return (
    <>
      <span className="inline-flex items-center gap-2">
        <TsButton
          variant={variant}
          size={size}
          onClick={() => setOpen(true)}
          data-testid={`device-session-button-${cameraId}`}
        >
          {OPEN_DEVICE_LABEL}
        </TsButton>
        <UseHttpsCheckbox
          checked={https.checked}
          onChange={https.setChosen}
          testId={`device-session-https-${cameraId}`}
        />
      </span>
      <DeviceSessionDialog
        open={open}
        onClose={() => setOpen(false)}
        target={target}
      />
    </>
  );
};

/**
 * "Open NVR" / "Open recorder": a session on the site's server, on its
 * auto-add port (admin picks the port). Shown when the site About says
 * `server_open_device_available`.
 */
export const ServerDeviceSessionButton = ({
  available,
  siteId,
  siteName,
  integrationType,
  defaultPort,
  defaultScheme,
  size = "sm",
  variant = "outline",
}: {
  available: boolean | null | undefined;
  siteId: number;
  siteName?: string | null;
  integrationType?: string | null;
  /** The server port / scheme it will open, when known: "Use HTTPS" default. */
  defaultPort?: number | null;
  defaultScheme?: string | null;
  size?: "sm" | "md";
  variant?: "outline" | "quiet";
}) => {
  const [open, setOpen] = React.useState(false);
  const https = useStartScheme(defaultPort, defaultScheme);
  const label = serverOpenDeviceLabel(integrationType);
  const target = React.useMemo<DeviceSessionTarget>(
    () => ({
      siteId,
      source: "server",
      ref: "",
      label: siteName
        ? `${siteName} ${
            isNvrDvrIntegration(integrationType) ? "NVR" : "recorder"
          }`
        : SOURCE_LABEL.server,
      ...(https.scheme ? { scheme: https.scheme } : {}),
    }),
    [siteId, siteName, integrationType, https.scheme]
  );
  if (!isDeviceSessionAvailable(available) || !Number.isFinite(siteId)) {
    return null;
  }
  return (
    <>
      <span className="inline-flex items-center gap-2">
        <TsButton
          variant={variant}
          size={size}
          onClick={() => setOpen(true)}
          data-testid="server-device-session-button"
        >
          {label}
        </TsButton>
        <UseHttpsCheckbox
          checked={https.checked}
          onChange={https.setChosen}
          testId="server-device-session-https"
        />
      </span>
      <DeviceSessionDialog
        open={open}
        onClose={() => setOpen(false)}
        target={target}
      />
    </>
  );
};

type TargetsLoad =
  | { status: "loading" }
  | { status: "ok"; targets: DeviceSessionTarget[] }
  | { status: "error" };

const DeviceTargetPicker = ({
  open,
  onClose,
  siteId,
  siteName,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  siteId: number;
  siteName?: string | null;
  onPick: (target: DeviceSessionTarget) => void;
}) => {
  const [load, setLoad] = React.useState<TargetsLoad>({ status: "loading" });
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoad({ status: "loading" });
    fetchSiteDeviceTargets(siteId)
      .then((targets) => !cancelled && setLoad({ status: "ok", targets }))
      .catch(() => !cancelled && setLoad({ status: "error" }));
    return () => {
      cancelled = true;
    };
  }, [open, siteId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{ fontFamily: "Mulish", fontWeight: 800, color: "#001943", pr: 6 }}
      >
        Open a device
        {siteName ? (
          <span className="block font-mulish font-normal text-[14px] text-[#4c5257]">
            {siteName}
          </span>
        ) : null}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <p className="m-0 mb-3 font-mulish text-[13px] text-[#4c5257]">
          Open a device&apos;s web page from our network, the way this
          site&apos;s connector reaches it. One device per session.
        </p>
        {load.status === "loading" && (
          <div className="flex justify-center py-6" role="status">
            <CircularProgress size={24} sx={{ color: "#001943" }} />
          </div>
        )}
        {load.status === "error" && (
          <p
            className="m-0 font-mulish text-[14px] text-[#b3261e]"
            role="alert"
          >
            We couldn&apos;t load this site&apos;s devices. Try again in a
            minute.
          </p>
        )}
        {load.status === "ok" && load.targets.length === 0 && (
          <p
            className="m-0 font-mulish text-[14px] text-[#4c5257]"
            data-testid="device-target-empty"
          >
            This site has no devices with a saved address to open.
          </p>
        )}
        {load.status === "ok" && load.targets.length > 0 && (
          <ul className="m-0 p-0 list-none divide-y divide-[#e5e7eb]">
            {load.targets.map((target) => (
              <li
                key={`${target.source}:${target.ref}`}
                className="flex items-center justify-between gap-3 py-2"
                data-testid={`device-target-${target.source}-${
                  target.ref || "site"
                }`}
              >
                <div className="min-w-0">
                  <p className="m-0 font-mulish font-bold text-[14px] text-actuate-blue truncate">
                    {target.label}
                  </p>
                  <p className="m-0 font-mulish text-[12px] text-[#4c5257]">
                    {SOURCE_LABEL[target.source]}
                    {target.address ? ` · ${target.address}` : ""}
                  </p>
                </div>
                <TsButton
                  variant="outline"
                  size="sm"
                  onClick={() => onPick(target)}
                >
                  Open
                </TsButton>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const SiteDeviceSessionButton = ({
  available,
  siteId,
  siteName,
  size = "sm",
  variant = "outline",
}: {
  available: boolean | null | undefined;
  siteId: number;
  siteName?: string | null;
  size?: "sm" | "md";
  variant?: "outline" | "quiet";
}) => {
  const [picking, setPicking] = React.useState(false);
  const [target, setTarget] = React.useState<DeviceSessionTarget | null>(null);
  if (!isDeviceSessionAvailable(available) || !Number.isFinite(siteId)) {
    return null;
  }
  return (
    <>
      <TsButton
        variant={variant}
        size={size}
        onClick={() => setPicking(true)}
        data-testid="site-device-session-button"
      >
        Open a device…
      </TsButton>
      <DeviceTargetPicker
        open={picking}
        onClose={() => setPicking(false)}
        siteId={siteId}
        siteName={siteName}
        onPick={(picked) => {
          setPicking(false);
          setTarget(picked);
        }}
      />
      <DeviceSessionDialog
        open={target != null}
        onClose={() => setTarget(null)}
        target={target}
      />
    </>
  );
};
